import type { AvatarTone, RosterEntry } from '@/types/domain';

const TONES: AvatarTone[] = ['sage', 'ochre', 'clay', 'slate'];
const UNITS = ['ER', 'ICU', 'OPD General', 'Ward 5', 'Ward 7', 'OR', 'LR', 'Pediatrics', 'CCU', 'Dialysis'];
const FIRST = ['Anong', 'Krit', 'Mali', 'Phai', 'Suda', 'Wira', 'Nattaya', 'Somchai', 'Pim', 'Tanawat', 'Kanya', 'Decha', 'Ploy', 'Nirun', 'Ratana', 'Chai', 'Wan', 'Sukanya', 'Apinya', 'Boon', 'Manee', 'Surasak', 'Jintana', 'Lek', 'Nok', 'Daeng', 'Fon', 'Aof', 'Mint', 'Bee', 'Golf', 'Aum', 'Tukta', 'Nan', 'Beam', 'Earth', 'Praew', 'Gun', 'View', 'Ice'];
const LAST = ['Pakdee', 'Suksai', 'Chan', 'Rojana', 'Meechai', 'Thongdee', 'Srisuk', 'Boonmee', 'Wong', 'Charoen', 'Phongphan', 'Suksri', 'Inta', 'Kaewkla', 'Maneerat', 'Rattana', 'Saetang', 'Bunnag', 'Pansiri', 'Yodsuwan'];
const TFIRST = ['อนงค์', 'กฤต', 'มะลิ', 'ไพร', 'สุดา', 'วีระ', 'ณัฐญา', 'สมชาย', 'พิม', 'ธนวัฒน์', 'กัญญา', 'เดชา', 'พลอย', 'นิรันดร์', 'รัตนา', 'ชัย', 'วรรณ', 'สุกัญญา', 'อภิญญา', 'บุญ', 'มณี', 'สุรศักดิ์', 'จินตนา', 'เล็ก', 'นก', 'แดง', 'ฝน', 'เอ้', 'มิ้นท์', 'บี', 'กอล์ฟ', 'อุ้ม', 'ตุ๊กตา', 'แนน', 'บีม', 'เอิร์ธ', 'แพรว', 'กัน', 'วิว', 'ไอซ์'];
const TLAST = ['ภักดี', 'สุขสาย', 'จันทร์', 'โรจนา', 'มีชัย', 'ทองดี', 'ศรีสุข', 'บุญมี', 'วงศ์', 'เจริญ', 'พงษ์พันธุ์', 'สุขศรี', 'อินตา', 'แก้วกล้า', 'มณีรัตน์', 'รัตนะ', 'แซ่ตั้ง', 'บุนนาค', 'ปานศิริ', 'ยอดสุวรรณ'];

/**
 * A believable ACLS class (~55) for the big-screen simulation. Deterministic
 * (no Math.random at load) so the wall renders stably across refetches.
 * Distribution: ~80% pass, ~10 below-pass, three perfect scores, two big
 * improvers (for 🚀 Most Improved), five still testing (posttest = null).
 */
function makeRoster(): RosterEntry[] {
  const N = 55;
  const out: RosterEntry[] = [];
  for (let i = 0; i < N; i++) {
    const finished = i < 50;
    let pretest: number | null = 42 + ((i * 13) % 50); // 42..91
    let posttest: number | null;
    if (!finished) posttest = null;
    else if (i === 3 || i === 22 || i === 41) posttest = 100; // 💯 perfect
    else if (i % 5 === 4) posttest = 60 + ((i * 3) % 18); // below pass: 60..77
    else posttest = 82 + ((i * 7) % 18); // pass: 82..99
    if (i === 7 || i === 30) { pretest = 48; posttest = 95; } // 🚀 big improvers (+47)
    if (posttest != null && pretest != null && pretest > posttest) pretest = Math.max(30, posttest - 8);

    const pass = posttest != null && posttest >= 80;
    out.push({
      name: `${FIRST[i % FIRST.length]} ${LAST[(i * 7) % LAST.length]}`,
      th: `${TFIRST[i % TFIRST.length]} ${TLAST[(i * 7) % TLAST.length]}`,
      unit: UNITS[i % UNITS.length],
      emp: `N-${30001 + i}`,
      tone: TONES[(i * 3) % TONES.length],
      lessons: 100,
      attended: finished,
      pretest,
      posttest,
      eval: pass,
      cert: pass ? 'ready' : 'blocked',
    });
  }
  return out;
}

/** Instructor roster for a class — completion + certificate state per learner. */
export const roster: RosterEntry[] = makeRoster();
